import { Check, Copy, Sparkles, User, Users } from "lucide-react";
import { useState } from "react";
import { Markdown } from "@/components/shared/Markdown";
import { useAuth } from "@/context/AuthContext";
import type { GroupChatMessage, SpecialistAgent } from "@/types";
import { AgentAvatar } from "./AgentAvatar";

interface GroupChatMessageBubbleProps {
  message: GroupChatMessage;
  agents?: SpecialistAgent[];
  onQuickAction?: (action: string) => void;
}

export function GroupChatMessageBubble({ message, agents = [], onQuickAction }: Readonly<GroupChatMessageBubbleProps>) {
  const { profile } = useAuth();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const user = profile?.user;

  const isSynthesis =
    message.senderName === "Synthesis" || (message.messageMetadata as Record<string, unknown>)?.is_synthesis === true;

  const handleCopy = async (content: string, messageUuid: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageUuid);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // User message
  if (message.senderType === "user") {
    return (
      <div className="flex gap-4">
        <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-content-primary">{user?.displayName || "You"}</span>
            <span className="text-xs text-content-quaternary">{new Date(message.createdAt).toLocaleTimeString()}</span>
          </div>
          <div className="text-content-secondary whitespace-pre-wrap wrap-break-word">{message.content}</div>
        </div>
      </div>
    );
  }

  // Synthesis message — distinct card with gradient accent
  if (message.senderType === "orchestrator" && isSynthesis) {
    return (
      <div className="flex gap-4 justify-center my-2">
        <div className="max-w-3xl w-full bg-linear-to-r from-mutual-orange/10 to-mutual-purple/10 border border-mutual-orange/30 rounded-xl px-5 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-brand-primary shrink-0" />
            <span className="text-sm font-semibold text-brand-primary">Synthesis</span>
            <span className="text-xs text-content-quaternary">{new Date(message.createdAt).toLocaleTimeString()}</span>
          </div>
          <div className="text-content-secondary text-sm leading-relaxed">
            <Markdown content={message.content} />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => handleCopy(message.content, message.uuid)}
              className="p-1.5 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
              title="Copy synthesis"
            >
              {copiedId === message.uuid ? (
                <Check className="w-3.5 h-3.5 text-green-400" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Orchestrator message (non-synthesis)
  if (message.senderType === "orchestrator") {
    return (
      <div className="flex gap-4 justify-center">
        <div className="max-w-2xl flex items-center gap-3 bg-surface-secondary/50 border border-border-secondary/50 rounded-lg px-4 py-2">
          <Users className="w-4 h-4 text-content-tertiary shrink-0" />
          <div className="text-sm text-content-tertiary italic">{message.content}</div>
        </div>
      </div>
    );
  }

  // Agent message - match by name since senderId is a DB integer but agent.uuid is a UUID string
  const agent = agents.find((a) => a.name === message.senderName);

  return (
    <div className="flex gap-4">
      {agent ? (
        <AgentAvatar agent={agent} size="md" />
      ) : (
        <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center shrink-0">
          <Users className="w-5 h-5 text-white" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="font-semibold text-content-primary">{message.senderName || "Persona"}</span>
          {agent && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-surface-secondary text-content-tertiary">
              {agent.role}
            </span>
          )}
          <span className="text-xs text-content-quaternary">{new Date(message.createdAt).toLocaleTimeString()}</span>
          {message.tokensUsed && (
            <span className="text-xs text-content-quaternary">• {message.tokensUsed.toLocaleString()} tokens</span>
          )}
        </div>
        <Markdown content={message.content} />
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => handleCopy(message.content, message.uuid)}
            className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
            title="Copy"
          >
            {copiedId === message.uuid ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          </button>
          {/* Quick action buttons for agent messages */}
          {agent && onQuickAction && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => onQuickAction(`@${agent.name} Can you elaborate on that point?`)}
                className="text-xs px-2 py-1 rounded-md bg-surface-secondary hover:bg-interactive-active text-content-tertiary hover:text-content-primary transition"
              >
                Elaborate
              </button>
              <button
                onClick={() => onQuickAction(`Challenge ${agent.name}'s perspective on this`)}
                className="text-xs px-2 py-1 rounded-md bg-surface-secondary hover:bg-interactive-active text-content-tertiary hover:text-content-primary transition"
              >
                Challenge
              </button>
              <button
                onClick={() => onQuickAction(`I'd like to hear more from the ${agent.name} on this topic`)}
                className="text-xs px-2 py-1 rounded-md bg-surface-secondary hover:bg-interactive-active text-content-tertiary hover:text-content-primary transition"
              >
                More from {agent.name}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
